/**
 * Blocking re-prompt modal that appears whenever a logged-in user
 * has not yet accepted the currently active version of every legal
 * document (Task #204).
 *
 * Behaviour:
 *  - Mounted inside the authenticated `Layout` so it never renders on
 *    public marketing pages or the auth flow (those routes don't go
 *    through `Layout`).
 *  - Suppressed for admins on `/admin` so the admin who just published
 *    a new version doesn't get prompted in the same click.
 *  - Calls `GET /api/v1/me/legal-consent-status` on mount + on each
 *    route change. If the response is non-empty, opens a non-
 *    dismissible modal that stops the user interacting with the page
 *    behind it.
 *  - "I agree" posts to `POST /api/v1/me/legal-consent` for all
 *    outstanding doc types in one call, then invalidates the status
 *    query so the modal closes.
 *  - "Sign out" is offered as a secondary action so the user is never
 *    trapped.
 *
 *  We deliberately render a plain fixed-position overlay rather than
 *  the shared `Dialog` component because the latter ships a built-in
 *  close-X and dismisses on overlay click / Escape — every one of
 *  which would defeat "blocking, non-dismissible".
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import {
  useLogout,
  useGetMyLegalConsentStatus,
  useAcceptMyLegalConsent,
  getGetMyLegalConsentStatusQueryKey,
  type LegalDocType,
} from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";

const DOC_LABEL: Record<LegalDocType, string> = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
};

const DOC_PATH: Record<LegalDocType, string> = {
  privacy: "/privacy",
  terms: "/terms",
};

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function LegalConsentGate() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: logout } = useLogout();

  // Suppress for admins on the admin tab so the admin who just published
  // a new version doesn't get prompted in the same click. Once they
  // navigate elsewhere in the app the modal will appear normally.
  const suppressed = useMemo(() => {
    if (!isAuthenticated || isLoading) return true;
    if (user?.isAdmin && location.startsWith("/admin")) return true;
    return false;
  }, [isAuthenticated, isLoading, user?.isAdmin, location]);

  const statusQueryKey = useMemo(() => getGetMyLegalConsentStatusQueryKey(), []);

  const { data, refetch } = useGetMyLegalConsentStatus({
    query: {
      enabled: !suppressed,
      // Status is cheap (one or two index hits) and the cost of missing
      // a freshly-published version is the user keeps using the old
      // policy — refresh whenever the modal-eligible context changes.
      staleTime: 30_000,
      retry: 1,
    },
  });

  // Re-fetch on every in-app route change so a user who kept the tab
  // open all day sees a freshly-published policy on their next click.
  useEffect(() => {
    if (suppressed) return;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: refetch is stable; only re-run on location/suppressed change
  }, [location, suppressed]);

  const outstanding = data?.items ?? [];
  const hasOutstanding = !suppressed && outstanding.length > 0;

  const outstandingKey = outstanding
    .map((o) => `${o.docType}:${o.version}`)
    .sort()
    .join("|");
  const [hasConfirmed, setHasConfirmed] = useState(false);
  useEffect(() => {
    setHasConfirmed(false);
  }, [outstandingKey]);

  const acceptMutation = useAcceptMyLegalConsent({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: statusQueryKey });
      },
      onError: (e: Error) => {
        toast({
          title: "Couldn't record consent",
          description: e.message,
          variant: "destructive",
        });
      },
    },
  });

  // Lock the body scroll while the modal is open so the page behind
  // can't be operated via keyboard scrolling either.
  useEffect(() => {
    if (!hasOutstanding) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [hasOutstanding]);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      /* ignore — clear local state regardless */
    }
    queryClient.clear();
    window.location.href = `${import.meta.env.BASE_URL}login`;
  };

  if (!hasOutstanding) return null;

  const labels = outstanding.map((o) => DOC_LABEL[o.docType]).join(" and ");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-consent-title"
      data-testid="legal-consent-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/65 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg border border-border bg-card shadow-2xl">
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2
                id="legal-consent-title"
                className="text-lg font-semibold leading-tight"
              >
                We've updated our {labels}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Please review the changes and confirm to continue using AI
                Shoot Lab.
              </p>
            </div>
          </div>

          <ul className="space-y-4">
            {outstanding.map((o) => (
              <li
                key={o.docType}
                className="rounded-md border border-border bg-muted/30 p-4 space-y-2"
                data-testid={`legal-consent-doc-${o.docType}`}
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="font-medium">{DOC_LABEL[o.docType]}</div>
                  <div className="text-xs text-muted-foreground">
                    v{o.version} · effective {formatDate(o.effectiveDate)}
                  </div>
                </div>
                {o.summaryOfChanges && o.summaryOfChanges.trim().length > 0 ? (
                  <div className="text-sm whitespace-pre-wrap text-foreground/90">
                    {o.summaryOfChanges}
                  </div>
                ) : null}
                <a
                  href={`${import.meta.env.BASE_URL}${DOC_PATH[o.docType].replace(/^\//, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  Read the full {DOC_LABEL[o.docType]}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/20 p-3">
            <Checkbox
              id="legal-consent-confirm"
              checked={hasConfirmed}
              onCheckedChange={(v) => setHasConfirmed(v === true)}
              disabled={acceptMutation.isPending}
              data-testid="legal-consent-confirm-checkbox"
              className="mt-0.5"
            />
            <Label
              htmlFor="legal-consent-confirm"
              className="text-sm font-normal leading-snug cursor-pointer"
            >
              I have read and accept the updated {labels}.
            </Label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              disabled={acceptMutation.isPending}
              data-testid="legal-consent-signout"
            >
              Sign out
            </Button>
            <Button
              type="button"
              onClick={() =>
                acceptMutation.mutate({
                  data: {
                    acceptedDocTypes: outstanding.map((o) => o.docType),
                  },
                })
              }
              disabled={acceptMutation.isPending || !hasConfirmed}
              data-testid="legal-consent-agree"
            >
              {acceptMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              I agree
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
